import {
  select, call, put, all, takeEvery,
} from 'redux-saga/effects';
import { renewSessionRequest, logoutRequest } from '@redux/modules/authentication/actions';
import { REVIEW_AND_SUBMIT_COMMENTS } from 'constants/sections';
import { rootSelectors } from '@redux/modules/selectors';
import { getChildSubsectionKeys } from 'helpers/form';
import { api } from 'services';
import { isEmptyObj } from 'util/general';
import { saveTypeUnschema } from '../../../schema/schema';
import {
  SaveRequestAction,
  saveFailure,
  saveCancelled,
  saveSuccess,
  saveAllRequest,
  saveAllFailure,
  saveAllSuccess,
  saveSignaturesRequest,
  saveSignaturesFailure,
  saveSignaturesSuccess,
  submitRequest,
  submitSuccess,
  submitFailure,
  statusRequest,
  statusSuccess,
  statusFailure,
  submissionComplete,
} from './actions';
import * as ActionTypes from './actionTypes';
import { formSectionsSelector } from '../../../selectors/navigation';

export function* handleSaveRequest(payload: any) {
  if (payload === undefined) {
    // Some sections have no data (such as introduction) and will return
    // undefined. When that happens, we basically stop the save. However, when
    // the user nagivates to a section with no data and tries to save, it will
    // not save.
    yield put(saveCancelled());
    return;
  }

  try {
    // eslint-disable-next-line
    const response = yield call(api.save, payload);

    yield put(saveSuccess(new Date()));
  } catch (err) {
    yield put(saveFailure(err));
  }
}

/**
 * [Saga - Side Effect] getSavePayload
 *
 * This will get the payload for the target subsection.
 * @returns {*}
 */
export function* getSavePayload(subsectionKey: string) {
  const formSection = yield select(rootSelectors.form.getSubsection, subsectionKey);
  return yield saveTypeUnschema(subsectionKey, formSection.data);
}

/**
 * [Saga - Side Effect] handleSubsectionSave
 *
 * This will save the individual subsection.
 */
export function* handleSubsectionSave(subsectionKey: string) {
  const formSection = yield select(rootSelectors.form.getSubsection, subsectionKey);
  if (!isEmptyObj(formSection.data)) {
    yield handleSaveRequest(yield call(getSavePayload, subsectionKey));
  }
}

export function* getAllFormSubsections(includeReview = false) {
  const state = yield select();
  // Get All Sections as a flat list
  const sections = formSectionsSelector(state, includeReview);
  return sections;
}

export function* getAllFormPayload() {
  const payloads = [];
  const allSections = yield call(getAllFormSubsections);
  // Pass an array of payloads to the save endpoint
  // Get all Subsections as a payload
  for (const section of allSections) {
    if (!(section.key.includes('REVIEW') || section.key.includes('INTRO'))) {
      const formSection = yield select(rootSelectors.form.getSubsection, section.key);
      if (formSection) {
        const payload = saveTypeUnschema(section.key, formSection.data);
        payloads.push(payload);
      }
    }
  }
  return payloads;
}

export function* onStatusRequest() {
  try {
    yield put(statusRequest());
    // eslint-disable-next-line
    const response = yield call(api.status);

    yield put(statusSuccess(response));
    yield put(submissionComplete());
  } catch (err) {
    yield put(statusFailure(err));
  }
}

export function* onSubmitRequest() {
  try {
    yield put(submitRequest());
    // eslint-disable-next-line
    yield api.submit();

    yield put(submitSuccess());
    yield call(onStatusRequest);
  } catch (err) {
    yield put(submitFailure(err));
  }
}

export function* onSaveSignaturesRequest(signaturesPayload) {
  try {
    yield put(saveSignaturesRequest());
    // eslint-disable-next-line
    const response = yield call(api.save, signaturesPayload);

    yield put(saveSignaturesSuccess(new Date()));
    yield call(onSubmitRequest);
  } catch (err) {
    yield put(saveSignaturesFailure(err));
  }
}

/**
 * Saves all sections as a single call to the API, does not trigger any UI on success
 */
export function* onSaveAllRequest(signaturesPayload) {
  try {
    yield put(saveAllRequest());
    const payload = yield call(getAllFormPayload);
    // eslint-disable-next-line
    const response = yield call(api.save, payload);

    yield put(saveAllSuccess(new Date()));
    yield call(onSaveSignaturesRequest, signaturesPayload);
  } catch (err) {
    yield put(saveAllFailure(err));
  }
}

export function* onStartSubmission(action) {
  yield call(onSaveAllRequest, action?.payload);
}

export function* onSaveForPDFAllRequest() {
  try {
    const payload = yield call(getAllFormPayload);
    // eslint-disable-next-line
    const response = yield call(api.save, payload);

  } catch (err) {
    // TODO: need to follow similar model for PDF as we do for save
    console.warn('onSaveForPDFAllRequest');
  }
}
/**
 * [Saga - UI] handleSectionSave
 *
 * This will do the following:
 * 1. Refresh session when true. When the session timesout, it'll save. We don't
 *    want to refresh the session, otherwise, the timeout screen won't appear.
 * 2. When subsection === REVIEW, it'll save the entire section in a single API
 *    call.
 * 3. When subsection !== REVIEW, it'll only save the subsection.
 */
export function* onSaveRequest(action: SaveRequestAction) {
  const { sectionKey, subsectionKey, shouldRenewSession } = action.payload;

  try {
    if (shouldRenewSession) {
      try {
        const token = yield select(rootSelectors.authentication.getToken);
        if (token === undefined) {
          yield put(logoutRequest(true));
          return;
        }

        yield put(renewSessionRequest(token));
      } catch (errRef) {
        console.log('refresh error caught');
        yield put(logoutRequest(true));
        return;
      }
    }
    const formIsLoaded = yield select(rootSelectors.api.getFetchFormLoaded);
    if (!formIsLoaded || (sectionKey === '' && subsectionKey === '')) { return; }
    if (sectionKey === 'REVIEW_AND_SUBMIT' && subsectionKey !== REVIEW_AND_SUBMIT_COMMENTS) {
      yield put(saveSuccess(new Date()));
      return;
    }

    const formType = yield select(rootSelectors.application.getFormType);

    if (subsectionKey.includes('_REVIEW')) {
      const subsections = getChildSubsectionKeys(formType, sectionKey);

      let allData = yield all(
        subsections.map((subsectionKey: string) => call(getSavePayload, subsectionKey)) // eslint-disable-line no-shadow
      );
      allData = allData.filter((e: any) => e !== undefined);

      yield call(handleSaveRequest, allData);
    } else {
      yield call(handleSubsectionSave, subsectionKey);
    }
  } catch (err) {
    console.log('failed to set handleSectionSave', err);

    yield put(saveFailure(err));
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(ActionTypes.SAVE_REQUEST, onSaveRequest),
    takeEvery(ActionTypes.SAVE_ALL_FOR_PDF_REQUEST, onSaveForPDFAllRequest),
    takeEvery(ActionTypes.START_SUBMISSION, onStartSubmission)
  ]);
}
